"use client";

import {
  flexRender,
  rowPaginationFeature,
  rowSortingFeature,
  tableFeatures,
  useTable,
  type ColumnDef as TanStackColumnDef,
  type OnChangeFn,
  type PaginationState,
  type RowData,
  type SortingState,
} from "@tanstack/react-table";
import type { ReactNode } from "react";
import { cx } from "../cx.ts";
import { useI18n } from "../i18n.tsx";
import { Button } from "./button.tsx";
import { Icon } from "./icon.tsx";
import { Skeleton } from "./skeleton.tsx";
import { Pagination, type PaginationLabels } from "./pagination.tsx";

/**
 * The features this table registers. v9 bundles nothing automatically: a state
 * slice whose feature is absent is a type error, so sorting and pagination are
 * named here and the rest of the library is not paid for.
 */
const features = tableFeatures({ rowSortingFeature, rowPaginationFeature });

/**
 * A column of a `DataTable`, for the pages that declare one.
 *
 * v9's `ColumnDef` is keyed by the feature set the table was built from, which
 * would otherwise put `typeof features` in every page's import. Pages take this
 * alias instead and stay unaware that the table has a feature set at all.
 */
export type ColumnDef<T extends RowData, TValue = unknown> = TanStackColumnDef<
  typeof features,
  T,
  TValue
>;

/**
 * What a table row may be. v9 narrowed it from `unknown` to a record or an
 * array, so a component that forwards its own generic to `DataTable` has to
 * repeat the constraint and needs the type to say it with.
 */
export type { RowData };

/**
 * The list surface every table page uses. Server-side pagination and sorting,
 * because a restaurant chain's order history is not something to fetch whole
 * and sort in the browser.
 *
 * Column widths are never fixed here: a Khmer header runs roughly 30% longer
 * than its English draft, and a table sized on English is a table that clips
 * in Khmer (`10-build-plan.md` § Risks).
 */
export type DataTableProps<T extends RowData> = {
  data: T[];
  // `unknown` rather than a concrete cell type: a table mixes text, badges
  // and action columns, and pinning one value type would force a cast per
  // column at every call site.
  columns: ColumnDef<T, unknown>[];
  total: number;
  loading?: boolean;
  /**
   * A refetch behind results already on screen. `loading` is only true on a
   * cold load because the query keeps previous data, so without this a page
   * change or a filter looks like nothing happened at all.
   */
  busy?: boolean;
  error?: string | null;
  emptyMessage: ReactNode;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  pagination: PaginationState;
  onPaginationChange: OnChangeFn<PaginationState>;
  onRowClick?: (row: T) => void;
  labels: {
    previous: string;
    next: string;
    showing: (from: number, to: number, total: number) => string;
    sortAscending?: string;
    sortDescending?: string;
  };
  /**
   * Opts into the numbered footer with a rows-per-page control. Absent — which
   * is every caller that has not asked for it — keeps the previous/next footer,
   * so the operator console is not dragged along by a change made for the back
   * office. The strings live here rather than defaulting to English because
   * `untranslated-strings.test.ts` does not scan `packages/`, and an English
   * default would leak into Khmer with nothing to catch it.
   */
  pageLabels?: Omit<PaginationLabels, "previous" | "next" | "showing">;
  pageSizeOptions?: readonly number[];
  skeletonRows?: number;
};

/** Deterministic, so skeleton rows do not reshuffle on every render. */
const SKELETON_WIDTHS = ["w-20", "w-24", "w-10", "w-16", "w-20", "w-8"];

export function DataTable<T extends RowData>({
  data,
  columns,
  total,
  loading,
  busy,
  error,
  emptyMessage,
  sorting,
  onSortingChange,
  pagination,
  onPaginationChange,
  onRowClick,
  labels,
  pageLabels,
  pageSizeOptions,
  skeletonRows,
}: DataTableProps<T>) {
  const { isComplexScript } = useI18n();
  const table = useTable({
    features,
    data,
    columns,
    state: { sorting, pagination },
    onSortingChange,
    onPaginationChange,
    manualPagination: true,
    manualSorting: true,
    // Off by default, on per column. TanStack would otherwise mark every
    // accessor column sortable, and `useListData` only forwards the columns
    // the server can actually sort on — so the affordance would appear on
    // columns where clicking it silently does nothing.
    //
    // A default rather than the table option `enableSorting: false`, which was
    // here until 2026-09-01 and turned sorting off everywhere: v9 ands the two
    // levels together (`enableSorting ?? true` on each), so the table's `false`
    // is final and a column asking for `true` never gets it. A default column
    // def is merged *under* each column, which is the "on per column" this
    // comment claimed all along.
    defaultColumn: { enableSorting: false },
    // Two states, not three. The list is sorted by the server and always has an
    // order, so the third click's "unsorted" would drop the header's arrow
    // while the rows stayed exactly where they were.
    enableSortingRemoval: false,
    rowCount: total,
  });

  const from = total === 0 ? 0 : pagination.pageIndex * pagination.pageSize + 1;
  const to = Math.min((pagination.pageIndex + 1) * pagination.pageSize, total);
  const pageCount = Math.max(1, Math.ceil(total / pagination.pageSize));
  const lastPage = pageCount - 1;
  const placeholderRows = skeletonRows ?? Math.min(pagination.pageSize, 8);

  return (
    <div className="overflow-hidden rounded-card border border-line bg-surface">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm" aria-busy={loading || busy || undefined}>
          <thead>
            {table.getHeaderGroups().map((group) => (
              <tr key={group.id} className="border-b border-line bg-surface-subtle">
                {group.headers.map((header) => {
                  const sortable = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  const content = header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext());
                  return (
                    <th
                      key={header.id}
                      scope="col"
                      aria-sort={
                        sortable
                          ? sorted === "asc"
                            ? "ascending"
                            : sorted === "desc"
                              ? "descending"
                              : "none"
                          : undefined
                      }
                      className={cx(
                        "px-4 py-2.5 text-start align-middle text-xs font-semibold uppercase text-ink-muted",
                        // Letter-spacing breaks cluster shaping in a complex
                        // script, and `uppercase` means nothing in Khmer
                        // anyway — the refinement is Latin-only.
                        !isComplexScript && "tracking-wide",
                      )}
                    >
                      {sortable ? (
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className="inline-flex items-center gap-1.5 text-start uppercase focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
                        >
                          {content}
                          <Icon
                            name={
                              sorted === "asc"
                                ? "chevron-up"
                                : sorted === "desc"
                                  ? "chevron-down"
                                  : "chevrons-up-down"
                            }
                            size={12}
                            className={sorted ? "text-primary" : "text-ink-subtle"}
                          />
                          {sorted ? (
                            <span className="sr-only">
                              {sorted === "asc" ? labels.sortAscending : labels.sortDescending}
                            </span>
                          ) : null}
                        </button>
                      ) : (
                        content
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody className={cx(busy && !loading && "opacity-60 transition-opacity duration-150")}>
            {error ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-10 text-center text-danger">
                  {error}
                </td>
              </tr>
            ) : loading && data.length === 0 ? (
              // Real rows at the real height, so nothing jumps when the data
              // lands. The previous version rendered a single "…" cell.
              Array.from({ length: placeholderRows }, (_, rowIndex) => (
                <tr key={`skeleton-${rowIndex}`} className="h-14 border-b border-line-subtle">
                  {columns.map((_column, cellIndex) => (
                    <td key={cellIndex} className="px-4 py-2 align-middle">
                      <Skeleton className={SKELETON_WIDTHS[cellIndex % SKELETON_WIDTHS.length]} />
                    </td>
                  ))}
                </tr>
              ))
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-12 text-center text-ink-muted">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                  className={cx(
                    // A row height rather than cell padding: CSS treats it as a
                    // minimum, so a wrapped Khmer cell grows the row instead of
                    // being clipped by it.
                    "h-14 border-b border-line-subtle transition-colors duration-150 last:border-0",
                    onRowClick && "cursor-pointer hover:bg-surface-subtle",
                  )}
                >
                  {row.getAllCells().map((cell) => (
                    <td key={cell.id} className="px-4 py-2 align-middle text-ink-body">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pageLabels ? (
        <Pagination
          pageIndex={pagination.pageIndex}
          pageCount={pageCount}
          from={from}
          to={to}
          total={total}
          pageSize={pagination.pageSize}
          pageSizeOptions={pageSizeOptions}
          onPageChange={(pageIndex) => onPaginationChange({ ...pagination, pageIndex })}
          // Back to the first page: staying on page 4 while the page grows can
          // land past the end of the result set, which reads as "no results".
          onPageSizeChange={(pageSize) => onPaginationChange({ pageIndex: 0, pageSize })}
          labels={{ ...pageLabels, ...labels }}
        />
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-4 py-3">
          <p className="text-sm text-ink-muted">{labels.showing(from, to, total)}</p>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={pagination.pageIndex === 0}
              onClick={() =>
                onPaginationChange({ ...pagination, pageIndex: pagination.pageIndex - 1 })
              }
            >
              {labels.previous}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={pagination.pageIndex >= lastPage}
              onClick={() =>
                onPaginationChange({ ...pagination, pageIndex: pagination.pageIndex + 1 })
              }
            >
              {labels.next}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
